import Fuse from 'fuse.js';
import {ReactNode, useMemo, useState} from 'react';

import {SidebarItemData} from './SidebarItemDatas';

interface FlattenedItem {
  item: SidebarItemData;
  searchText: string;
  originalIndex: number;
  /** Breadcrumb trail of ancestor titles (root-first, excluding the item itself). */
  breadcrumbTitles: Array<string>;
}

/**
 * Extracts a plain text string from a ReactNode for fuzzy search purposes.
 * Handles strings directly and falls back to empty string for complex ReactNodes
 * (e.g. JSX elements like `SendToDocumentSidebarItemTitle`). Items with non-string
 * titles will not appear in search results.
 */
function extractSearchText(node: ReactNode): string {
  if (typeof node === 'string') {
    return node;
  }
  return '';
}

/**
 * Builds a breadcrumb subtitle from an array of ancestor titles (excluding the matched item).
 * Shows up to 3 ancestors. If deeper, shows: `first ancestor / ... / last ancestor`.
 *
 * Returns null if there are no ancestors (top-level item).
 */
export function buildBreadcrumbSubtitle(breadcrumbTitles: Array<string>): string | null {
  if (breadcrumbTitles.length === 0) {
    return null;
  }
  if (breadcrumbTitles.length <= 3) {
    return breadcrumbTitles.join(' / ');
  }
  return `${breadcrumbTitles[0]} / ... / ${breadcrumbTitles[breadcrumbTitles.length - 1]}`;
}

/**
 * Recursively flattens sidebar item datas (including children) into a flat list,
 * preserving original index for tiebreaking in search results and tracking parent
 * hierarchy for breadcrumbs.
 */
function flattenSidebarItems(sections: Array<Array<SidebarItemData>>): Array<FlattenedItem> {
  const result: Array<FlattenedItem> = [];
  let index = 0;

  function flatten(items: Array<SidebarItemData>, ancestors: Array<string>): void {
    for (const item of items) {
      const titleText = extractSearchText(item.title);
      result.push({
        item,
        searchText: titleText,
        originalIndex: index++,
        breadcrumbTitles: ancestors,
      });
      if (item.childSidebarItems && item.childSidebarItems.length > 0) {
        const childAncestors = titleText ? [...ancestors, titleText] : ancestors;
        flatten(item.childSidebarItems, childAncestors);
      }
    }
  }

  for (const section of sections) {
    flatten(section, []);
  }

  return result;
}

export interface SearchResultItem {
  item: SidebarItemData;
  /** Breadcrumb subtitle showing parent hierarchy, or null for top-level items. */
  breadcrumbSubtitle: string | null;
}

export interface UseSidebarSearchOutput {
  query: string;
  setQuery: (query: string) => void;
  /** Filtered items when search is active, null when no active search. */
  filteredItems: SearchResultItem[] | null;
}

/**
 * Provides fuzzy search over sidebar items. Only items with plain string `title` values
 * are searchable — items with complex ReactNode titles (e.g. components that fetch data
 * via GraphQL) will not appear in search results.
 *
 * @package
 */
export default function useSidebarSearch(
  sectionItemArrays: Array<Array<SidebarItemData>>
): UseSidebarSearchOutput {
  const [query, setQuery] = useState('');

  const flattenedItems = useMemo(() => flattenSidebarItems(sectionItemArrays), [sectionItemArrays]);

  const fuse = useMemo(
    () =>
      new Fuse(flattenedItems, {
        keys: ['searchText'],
        threshold: 0.4,
        includeScore: true,
      }),
    [flattenedItems]
  );

  const filteredItems = useMemo(() => {
    if (!query.trim()) {
      return null;
    }

    const results = fuse.search(query);
    // Sort by best match score first, original flattened order as tiebreaker
    results.sort((a, b) => {
      const scoreDiff = (a.score ?? 1) - (b.score ?? 1);
      if (scoreDiff !== 0) {
        return scoreDiff;
      }
      return a.item.originalIndex - b.item.originalIndex;
    });

    return results.map((result) => ({
      item: result.item.item,
      breadcrumbSubtitle: buildBreadcrumbSubtitle(result.item.breadcrumbTitles),
    }));
  }, [query, fuse]);

  return {query, setQuery, filteredItems};
}
